import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Flex,
  Heading,
  Icon,
  Input,
  Select,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { FaSyncAlt } from 'react-icons/fa';
import { Header } from '../../components/Header';
import { SiderbarResponsive } from '../../components/SiderbarResponsive';
import ReactTableComponent from '../../components/TableComponent';
import { useSidebar } from '../../context/SidebarContext';
import { getColumn as getApontamentoColumn } from '../../utils/getApontamentoColumn';
import { isoDiasAtras } from '../../helpers/dataBr';
import {
  Apontamento,
  ModuloApontamentosIndisponivelError,
  contarApontamentosPorPessoa,
  listarApontamentos,
  usuarioDoApontamento,
} from '../../service/apontamentos';
import { ApontamentoModal } from './ApontamentoModal';
import { FilialPermitida, useFiliaisPermitidas } from '../../hooks/useFiliaisPermitidas';
import api from '../../service/api';

const PERIODOS = [
  { dias: 7, rotulo: 'Últimos 7 dias' },
  { dias: 30, rotulo: 'Últimos 30 dias' },
  { dias: 90, rotulo: 'Últimos 90 dias' },
  { dias: 0, rotulo: 'Todo o período' },
];

// O valor continua sendo o código de filial do ERP; só o rótulo fala "empresa",
// que é como a operação se refere a Suplen/NeuroVasc (pedido do usuário).
// Reserva para quando o cadastro de empresas não carrega: o filtro de verdade
// lista todas as filiais cadastradas (BSB, MG, MT...), não só estas duas.
const FILIAIS = [
  { valor: '', rotulo: 'Todas as empresas' },
  { valor: '0101', rotulo: 'Suplen (0101)' },
  { valor: '0301', rotulo: 'NeuroVasc (0301)' },
];

/**
 * Apontamentos registrados pelos vendedores — a visão do faturamento.
 *
 * Mostra o que foi apontado, por quem, com os materiais e os anexos (fotos das
 * etiquetas e a folha de consumo em PDF). A baixa das etiquetas em si acontece
 * no Protheus, direto do app.
 */
const Apontamentos = () => {
  const { larguraAtual } = useSidebar();
  const {
    filiais: filiaisPermitidas,
    codigos: codigosPermitidos,
    carregando: carregandoFiliais,
  } = useFiliaisPermitidas();
  const [apontamentos, setApontamentos] = useState<Apontamento[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [moduloIndisponivel, setModuloIndisponivel] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const [periodoDias, setPeriodoDias] = useState(30);
  const [filialFiltro, setFilialFiltro] = useState('');
  const [vendedorFiltro, setVendedorFiltro] = useState('');
  const [busca, setBusca] = useState('');
  const [selecionado, setSelecionado] = useState<Apontamento | null>(null);
  const [empresas, setEmpresas] = useState<FilialPermitida[] | null>(null);

  // Sem o prefixo /api-essencial/v1: as rotas de empresa ficam na raiz da API.
  useEffect(() => {
    let ativo = true;
    api
      .get('/empresas')
      .then(({ data }) => {
        if (!ativo) return;
        setEmpresas(
          (Array.isArray(data) ? data : []).filter(
            (empresa: any) => empresa?.empresa_id && !empresa?.excluido
          )
        );
      })
      .catch(() => {
        if (ativo) setEmpresas(null);
      });

    return () => {
      ativo = false;
    };
  }, []);

  const fetchData = useCallback(async () => {
    if (carregandoFiliais) return;
    setIsRefreshing(true);
    setErro(null);

    try {
      const dados = await listarApontamentos({
        filial: filialFiltro || undefined,
        desde: periodoDias ? isoDiasAtras(periodoDias) : undefined,
      });
      const permitidos = codigosPermitidos ? new Set(codigosPermitidos) : null;
      setApontamentos(
        permitidos
          ? dados.filter((item) => permitidos.has(String(item.filial || '').trim()))
          : dados
      );
      setModuloIndisponivel(false);
    } catch (e: any) {
      // Sem dados de exemplo: a tela diz o que aconteceu.
      setApontamentos([]);
      if (e instanceof ModuloApontamentosIndisponivelError) {
        setModuloIndisponivel(true);
      } else {
        setErro(e?.response?.data?.message || 'Não foi possível carregar os apontamentos.');
      }
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [filialFiltro, periodoDias, carregandoFiliais, codigosPermitidos]);

  const opcoesFiliais = useMemo(() => {
    // Logística enxerga só as filiais vinculadas; os demais, todas as cadastradas.
    const lista = filiaisPermitidas || empresas;
    if (!lista || lista.length === 0) {
      return filiaisPermitidas
        ? [{ valor: '', rotulo: 'Todas as empresas permitidas' }]
        : FILIAIS;
    }

    return [
      {
        valor: '',
        rotulo: filiaisPermitidas ? 'Todas as empresas permitidas' : 'Todas as empresas',
      },
      ...lista
        .filter((empresa) => String(empresa.filial || '').trim())
        .map((empresa) => {
          const codigo = String(empresa.filial).trim();
          return {
            valor: codigo,
            rotulo: `${empresa.fantasia || empresa.razao_social || 'Filial'} (${codigo})`,
          };
        })
        .sort((a, b) => a.valor.localeCompare(b.valor)),
    ];
  }, [filiaisPermitidas, empresas]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Só quem apontou na empresa e no período carregados, em ordem alfabética.
  const vendedores = useMemo(
    () => contarApontamentosPorPessoa(apontamentos)
      .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')),
    [apontamentos]
  );

  // Trocar a empresa ou o período pode tirar o vendedor escolhido da lista. Aí
  // o filtro deixa de valer (o select volta para "Todos") em vez de zerar a
  // tabela com uma escolha que nem aparece mais.
  const vendedorAtivo = vendedores.some((vendedor) => vendedor.usuarioId === vendedorFiltro)
    ? vendedorFiltro
    : '';

  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo && !vendedorAtivo) { return apontamentos; }

    return apontamentos.filter((item) => {
      if (vendedorAtivo && usuarioDoApontamento(item) !== vendedorAtivo) { return false; }
      if (!termo) { return true; }

      return [
        item.clientes?.fantasia,
        item.clientes?.razao_social,
        item.paciente,
        item.medico_nome,
        item.convenio_nome,
        item.usuarios?.name,
      ].some((campo) => String(campo || '').toLowerCase().includes(termo));
    });
  }, [apontamentos, busca, vendedorAtivo]);

  const colunas = useMemo(() => getApontamentoColumn(setSelecionado), []);

  return (
    <>
      <Header />
      <SiderbarResponsive />

      <Flex
        direction="column"
        ml={larguraAtual}
        mt="80px"
        minH="calc(100vh - 80px)"
        bg="gray.700"
        p={6}
      >
        <Flex justify="space-between" align="center" mb={6} gap={4} wrap="wrap">
          <Heading size="md">Apontamentos</Heading>
          <Flex align="center" gap={3} color="gray.400" fontSize="xs">
            {isRefreshing && <Icon as={FaSyncAlt} />}
            <Text>{filtrados.length} apontamento(s)</Text>
            {moduloIndisponivel && (
              <Badge colorScheme="yellow" variant="subtle" fontSize="10px">
                aguardando backend
              </Badge>
            )}
            <Button size="sm" colorScheme="orange" variant="outline" onClick={fetchData}>
              Atualizar
            </Button>
          </Flex>
        </Flex>

        <Flex gap={3} mb={5} wrap="wrap" align="center">
          <Select
            size="sm"
            w="200px"
            borderRadius="md"
            value={filialFiltro}
            onChange={(evento) => setFilialFiltro(evento.target.value)}
          >
            {opcoesFiliais.map((filial) => (
              <option key={filial.valor} value={filial.valor}>
                {filial.rotulo}
              </option>
            ))}
          </Select>

          <Select
            size="sm"
            w="200px"
            borderRadius="md"
            value={periodoDias}
            onChange={(evento) => setPeriodoDias(Number(evento.target.value))}
          >
            {PERIODOS.map((periodo) => (
              <option key={periodo.dias} value={periodo.dias}>
                {periodo.rotulo}
              </option>
            ))}
          </Select>

          <Select
            size="sm"
            w="200px"
            borderRadius="md"
            value={vendedorAtivo}
            onChange={(evento) => setVendedorFiltro(evento.target.value)}
          >
            <option value="">Todos os vendedores</option>
            {vendedores.map((vendedor) => (
              <option key={vendedor.usuarioId} value={vendedor.usuarioId}>
                {vendedor.nome}
              </option>
            ))}
          </Select>

          <Input
            size="sm"
            w="280px"
            borderRadius="md"
            placeholder="Hospital, paciente, médico, vendedor..."
            value={busca}
            onChange={(evento) => setBusca(evento.target.value)}
          />
        </Flex>

        {isLoading && (
          <Flex justify="center" py="10">
            <Spinner />
          </Flex>
        )}

        {!isLoading && moduloIndisponivel && (
          <Text color="gray.400" fontSize="sm">
            O registro de apontamentos ainda não está disponível nesta API.
          </Text>
        )}

        {!isLoading && erro && (
          <Text color="red.400" fontSize="sm">{erro}</Text>
        )}

        {!isLoading && !moduloIndisponivel && !erro && filtrados.length === 0 && (
          <Text color="gray.400" fontSize="sm">
            Nenhum apontamento no período selecionado.
          </Text>
        )}

        {!isLoading && filtrados.length > 0 && (
          <ReactTableComponent columns={colunas} data={filtrados} isPagenable />
        )}
      </Flex>

      {selecionado && (
        <ApontamentoModal apontamento={selecionado} onClose={() => setSelecionado(null)} />
      )}
    </>
  );
};

export default Apontamentos;
